/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/layout/Header.tsx
 *
 * Rôle :
 *   En-tête fixe affiché sur **toutes les pages** (sauf splash). Garantit
 *   l'affichage permanent des deux logos officiels imposés par la règle
 *   §8.4 (Région Académique Grand-Est + Académie Nancy-Metz). À droite,
 *   slot de navigation/icônes (notifications, profil) — réutilisable.
 *
 * Réutilisable :
 *   100 %. Le pattern « 2 logos institutionnels gauche + actions droite »
 *   est typique de toute app DRANE. Pour une autre app, remplacer les 2
 *   <img> + l'`appName`.
 *
 * Accessibilité :
 *   - <header> sémantique avec rôle implicite `banner`.
 *   - alt-text obligatoire sur les logos.
 *   - Lien Skip-to-content au tout début (cf. WCAG 2.4.1) — branché ici
 *     pour que toutes les pages en bénéficient.
 *
 * @author Mika
 * @since 2026-05-04
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { PhaseBadge } from './PhaseBadge';
import { useBrandingLogos } from '@/hooks/useBrandingLogos';
import { useEventIdentity } from '@/context/EventIdentityProvider';
import styles from './Header.module.css';

export interface HeaderProps {
  /** Slot droite — boutons icônes (notifications, profil, menu…). */
  actions?: ReactNode;
  /** Cible du Skip Link (id de l'élément main). Défaut `main-content`. */
  skipTargetId?: string;
}

export function Header({ actions, skipTargetId = 'main-content' }: HeaderProps) {
  // Logos de marque 100 % configurables au back-office (V72,
  // `branding.logo_*`) — aucun logo en dur (multi-salon). Repli = [].
  const logos = useBrandingLogos();
  // Nom du salon interpolé (LOT 3) — plus de « NOLAS 2026 » en dur dans le titre.
  const identity = useEventIdentity();

  return (
    <>
      {/* Skip link — visible uniquement au focus clavier (cf. base.css `.visually-hidden`) */}
      <a href={`#${skipTargetId}`} className={styles.skipLink}>
        Aller au contenu principal
      </a>

      <header className={styles.header} role="banner">
        <Link to="/" className={styles.brand} aria-label="Accueil TNE 2026">
          <span className={styles.brandTitle}>
            <FormattedMessage id="app.name" values={identity} />
          </span>
          {/* FIX 2026-05-06 (S19-B) — pastille phase événement (J-N / LIVE / Replays).
              Sous-composant isolé pour limiter le re-render au tic minute. */}
          <PhaseBadge />
        </Link>

        {/*
          Logos de marque — 100 % configurables au back-office (V72 :
          settings `branding.logo_{1..3}_url` / `_alt`, section « Marque »
          de /admin/settings). AUCUN logo en dur (multi-salon) : chaque
          salon renseigne les siens en une fraction de seconde, sans rebuild.
          Repli = aucun logo affiché (jamais un logo d'un autre salon).
         */}
        {logos.length > 0 && (
          <div className={styles.officialLogos} aria-label="Logos officiels">
            {logos.map((logo) => (
              <img
                key={logo.url}
                src={logo.url}
                alt={logo.alt}
                className={styles.logoOfficial}
                loading="eager"
                width={120}
                height={42}
              />
            ))}
          </div>
        )}

        {actions && <div className={styles.actions}>{actions}</div>}
      </header>
    </>
  );
}
